import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button } from "@air/primitive-button";
import { Modal, ModalCloseButton, ModalDescription, ModalTitle } from "@air/primitive-modal";
import { useToasts } from "@air/provider-toast";
import { reportErrorToBugsnag } from "@air/utils-error";
import { memo, useCallback } from "react";
import { useSelector } from "react-redux";
import { PrivateBoardSearch } from "~/components/BoardSearch/PrivateBoardSearch";
import { useSingleBoardSearchState } from "~/components/BoardSearch/shared/hooks";
import { ToastLink } from "~/components/UI/ToastLink";
import { usePrivateBoardsSetBoardCoverMutation } from "~/components/v2/hooks/queries/mutations/usePrivateBoardsSetBoardCoverMutation";
import { useBoardPermissionsCache } from "~/hooks/useBoardPermissionsCache";
import { useGoToBoardPage } from "~/hooks/useGoToBoardPage";
import { useShowSubscriptionExpiredModal } from "~/hooks/useShowSubscriptionExpiredModal";
import { centralizedBoardSelector } from "~/store/centralizedBoard/selectors";
import { canChangeBoardDetails } from "~/utils/permissions/boardPermissions";
export const BoardCoverSelectModal = /*#__PURE__*/ memo((param)=>{
    let { onClose, trackLocation, clip } = param;
    const { goToBoardPage } = useGoToBoardPage();
    const { mutate: setBoardCoverMutation, isPending: isSettingCover } = usePrivateBoardsSetBoardCoverMutation();
    const { showToast } = useToasts();
    const currentBoard = useSelector(centralizedBoardSelector);
    const { showingSubscriptionExpiredModal } = useShowSubscriptionExpiredModal({
        onClose
    });
    const { getBoardPermissions } = useBoardPermissionsCache();
    const { selectedBoard, isBoardSelected, shouldShowSubBoards, onSelectBoard, isLibrarySelected, shouldShowLibraryBoards, onSelectLibrary } = useSingleBoardSearchState({
        initialBoard: currentBoard
    });
    const onBoardSelectChange = useCallback((param)=>{
        let { board, selected } = param;
        onSelectBoard(selected ? board : null);
    }, [
        onSelectBoard
    ]);
    const getIsBoardDisabled = useCallback((board)=>{
        const boardPermissions = getBoardPermissions(board.id);
        if (!canChangeBoardDetails(boardPermissions)) {
            return {
                isDisabled: true,
                message: "You do not have permission to edit this board"
            };
        }
    }, [
        getBoardPermissions
    ]);
    const goToBoard = useCallback((board)=>{
        goToBoardPage({
            board,
            trackLocation
        });
    }, [
        goToBoardPage,
        trackLocation
    ]);
    const isUpdatingCurrentBoard = useCallback((board)=>board.id === (currentBoard === null || currentBoard === void 0 ? void 0 : currentBoard.id), [
        currentBoard === null || currentBoard === void 0 ? void 0 : currentBoard.id
    ]);
    const showConfirmationToast = useCallback((board)=>{
        showToast(/*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                !isUpdatingCurrentBoard(board) ? /*#__PURE__*/ _jsx(ToastLink, {
                    onClick: ()=>goToBoard(board),
                    children: board.title
                }) : board.title,
                " cover has been updated"
            ]
        }));
    }, [
        showToast,
        isUpdatingCurrentBoard,
        goToBoard
    ]);
    const handleSetCoverClick = useCallback(()=>{
        var _clip_assets;
        if (!selectedBoard) {
            throw reportErrorToBugsnag({
                error: new Error("No board selected"),
                context: "BoardCoverSelectModal: Attempted to set cover without selected board",
                metadata: {
                    clipId: clip.id,
                    workspaceId: clip.workspaceId
                }
            });
        }
        setBoardCoverMutation({
            boardId: selectedBoard.id,
            workspaceId: clip.workspaceId,
            clipId: clip.id,
            thumbnailUrl: (_clip_assets = clip.assets) === null || _clip_assets === void 0 ? void 0 : _clip_assets.image,
            // Use default region (full image)
            region: {
                top: 0,
                left: 0,
                width: 100,
                height: 100
            },
            // Pass the full board data if we're updating the current board
            board: selectedBoard.id === (currentBoard === null || currentBoard === void 0 ? void 0 : currentBoard.id) ? currentBoard : undefined,
            trackLocation
        }, {
            onSuccess: ()=>{
                showConfirmationToast(selectedBoard);
                onClose();
            },
            onError: ()=>{
                showToast("Failed to set board cover. Please try again.");
            }
        });
    }, [
        selectedBoard,
        setBoardCoverMutation,
        clip,
        currentBoard,
        trackLocation,
        showConfirmationToast,
        onClose,
        showToast
    ]);
    const buttonsDisabled = !isBoardSelected || isSettingCover;
    if (showingSubscriptionExpiredModal) {
        return null;
    }
    return /*#__PURE__*/ _jsxs(Modal, {
        "data-testid": "SET_BOARD_COVER_MODAL",
        dangerouslyBypassFocusLock: true,
        isOpen: true,
        onDismiss: onClose,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "mb-3 flex justify-between",
                children: [
                    /*#__PURE__*/ _jsx(ModalTitle, {
                        children: "Set board cover"
                    }),
                    /*#__PURE__*/ _jsx(ModalCloseButton, {
                        className: "shrink-0",
                        onClick: onClose
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(ModalDescription, {
                className: "mb-4",
                children: "Select a board to set this image as its cover"
            }),
            /*#__PURE__*/ _jsx(PrivateBoardSearch, {
                isBoardSelected: isBoardSelected,
                onBoardSelectChange: onBoardSelectChange,
                shouldShowSubBoards: shouldShowSubBoards,
                initialBoardId: currentBoard === null || currentBoard === void 0 ? void 0 : currentBoard.id,
                isLibrarySelected: isLibrarySelected,
                shouldShowLibraryBoards: shouldShowLibraryBoards,
                onSelectLibrary: onSelectLibrary,
                shouldExpandLibraryInsteadOfSelect: true,
                getIsBoardDisabled: getIsBoardDisabled
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "mt-6 flex justify-end gap-3",
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        appearance: "ghost",
                        color: "grey",
                        onClick: onClose,
                        size: "large",
                        children: "Cancel"
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        appearance: "filled",
                        color: "blue",
                        disabled: buttonsDisabled,
                        onClick: handleSetCoverClick,
                        size: "large",
                        children: isSettingCover ? "Setting..." : "Set cover"
                    })
                ]
            })
        ]
    });
});
BoardCoverSelectModal.displayName = "BoardCoverSelectModal";
